// ============================================================================
//  particle-mesh.js — hero topographic particle mesh (three.js)
//
//  A gold particle-and-wireframe terrain that spans the whole hero. A layered
//  height field drifts continuously, and the pointer raises a soft swell + ripple
//  that follows the cursor across the surface. Fog fades the far edges so the
//  terrain dissolves into the dark like a contour map.
//
//   - reducedMotion → render one static frame, no loop, no pointer.
//   - IntersectionObserver pauses the RAF loop when the hero is off-screen.
//   - WebGL failure degrades silently (the hero image remains).
//   - three.js is imported lazily so it stays out of the critical path.
// ============================================================================

export async function initParticleMesh({ reducedMotion = false } = {}) {
  const mount = document.querySelector('[data-particle-mesh]');
  if (!mount) return;

  const THREE = await import('three');

  const vw = () => mount.clientWidth || window.innerWidth;
  const vh = () => mount.clientHeight || window.innerHeight;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  } catch (err) {
    return; // no WebGL — leave the hero image as-is
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(vw(), vh());
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  // Light fog so the far terrain fades gently toward the top of the hero
  // instead of vanishing mid-frame.
  scene.fog = new THREE.FogExp2(0x0a2540, 0.019);

  const camera = new THREE.PerspectiveCamera(60, vw() / vh(), 0.1, 140);
  camera.position.set(0, 8, 12);
  camera.lookAt(0, -1.4, -20);

  // --- Grid -----------------------------------------------------------------
  // The plane is offset in Z (near edge just in front of the camera, receding
  // far into the distance) and wide enough that the terrain fills the frame all
  // the way to the top.
  const small = Math.min(vw(), vh()) < 700;
  const COLS = small ? 66 : 104;
  const ROWS = small ? 54 : 82;
  const SIZE_X = 118;
  const SIZE_Z = 56;
  const Z_NEAR = 8; // nearest row, just ahead of the camera (z = 12)
  const Z_FAR = Z_NEAR - SIZE_Z;
  const N = COLS * ROWS;

  const gx = (c) => (c / (COLS - 1) - 0.5) * SIZE_X;
  const gz = (r) => Z_NEAR - (r / (ROWS - 1)) * SIZE_Z;

  // Point cloud (grid vertices).
  const pointPos = new Float32Array(N * 3);
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const i = (r * COLS + c) * 3;
      pointPos[i] = gx(c);
      pointPos[i + 1] = 0;
      pointPos[i + 2] = gz(r);
    }
  }
  const pointGeo = new THREE.BufferGeometry();
  pointGeo.setAttribute('position', new THREE.BufferAttribute(pointPos, 3));
  const pointMat = new THREE.PointsMaterial({
    color: 0xd8c489,
    size: 0.06,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
    fog: true,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(pointGeo, pointMat);
  scene.add(points);

  // Wireframe grid (edges) → the "mesh" between particles.
  const hEdges = (COLS - 1) * ROWS;
  const vEdges = COLS * (ROWS - 1);
  const linePos = new Float32Array((hEdges + vEdges) * 2 * 3);
  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute('position', new THREE.BufferAttribute(linePos, 3));
  const lineMat = new THREE.LineBasicMaterial({
    color: 0xb99a55,
    transparent: true,
    opacity: 0.2,
    fog: true,
  });
  const lines = new THREE.LineSegments(lineGeo, lineMat);
  scene.add(lines);

  // --- Height field ---------------------------------------------------------
  const heights = new Float32Array(N);

  function computeHeights(t, mx, mz, mAmp) {
    for (let r = 0; r < ROWS; r++) {
      const z = gz(r);
      // front: 0 at the far edge, 1 at the near edge (closest to camera).
      const front = (z - Z_FAR) / SIZE_Z;
      // Ease the drift speed down toward the front so the nearest particles
      // move slowly and settle; the distance keeps a gentle flow.
      const te = t * (0.42 - 0.3 * front);
      const amp = 1 - 0.3 * front; // calmer swells up front, too

      for (let c = 0; c < COLS; c++) {
        const x = gx(c);
        let hgt =
          amp *
          (0.9 * Math.sin(x * 0.33 + te) * Math.cos(z * 0.28 - te * 0.8) +
            0.5 * Math.sin(x * 0.7 - te * 0.85 + z * 0.2) +
            0.35 * Math.cos(z * 0.9 + te * 0.6) +
            0.25 * Math.sin((x + z) * 0.5 - te * 1.1));

        if (mAmp > 0.001) {
          const dx = x - mx;
          const dz = z - mz;
          const d2 = dx * dx + dz * dz;
          hgt += mAmp * 2.6 * Math.exp(-d2 / 10); // swell under the cursor
          hgt += mAmp * 0.55 * Math.sin(Math.sqrt(d2) * 1.2 - t * 1.0) * Math.exp(-d2 / 28); // slow ripple
        }
        heights[r * COLS + c] = hgt;
      }
    }
  }

  function applyHeights() {
    for (let i = 0; i < N; i++) pointPos[i * 3 + 1] = heights[i];
    pointGeo.getAttribute('position').needsUpdate = true;

    let o = 0;
    // Horizontal edges.
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS - 1; c++) {
        const a = r * COLS + c;
        const b = a + 1;
        linePos[o++] = pointPos[a * 3]; linePos[o++] = heights[a]; linePos[o++] = pointPos[a * 3 + 2];
        linePos[o++] = pointPos[b * 3]; linePos[o++] = heights[b]; linePos[o++] = pointPos[b * 3 + 2];
      }
    }
    // Vertical edges.
    for (let r = 0; r < ROWS - 1; r++) {
      for (let c = 0; c < COLS; c++) {
        const a = r * COLS + c;
        const b = a + COLS;
        linePos[o++] = pointPos[a * 3]; linePos[o++] = heights[a]; linePos[o++] = pointPos[a * 3 + 2];
        linePos[o++] = pointPos[b * 3]; linePos[o++] = heights[b]; linePos[o++] = pointPos[b * 3 + 2];
      }
    }
    lineGeo.getAttribute('position').needsUpdate = true;
  }

  const resize = () => {
    renderer.setSize(vw(), vh());
    camera.aspect = vw() / vh();
    camera.updateProjectionMatrix();
  };
  window.addEventListener('resize', resize, { passive: true });

  // --- Reduced motion: one static frame ------------------------------------
  if (reducedMotion) {
    computeHeights(0, 0, 0, 0);
    applyHeights();
    renderer.render(scene, camera);
    return;
  }

  // --- Pointer interaction --------------------------------------------------
  const pointer = new THREE.Vector2(0, 0);
  const raycaster = new THREE.Raycaster();
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const hitPoint = new THREE.Vector3();
  const mouseWorld = new THREE.Vector3(0, 0, 0);
  let mAmp = 0;
  let mAmpTarget = 0;

  const onPointerMove = (e) => {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    mAmpTarget = 1;
  };
  const onPointerLeave = () => {
    mAmpTarget = 0;
  };
  window.addEventListener('pointermove', onPointerMove, { passive: true });
  window.addEventListener('pointerleave', onPointerLeave, { passive: true });
  window.addEventListener('blur', onPointerLeave);

  let last = performance.now();
  let elapsed = 0;
  let running = true;
  let rafId = 0;

  const frame = (now) => {
    if (!running) return;
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    elapsed += dt;

    // Ease the pointer swell in/out and track its world position — both slow
    // so the surface responds gently rather than snapping to the cursor.
    mAmp += (mAmpTarget - mAmp) * 0.035;
    raycaster.setFromCamera(pointer, camera);
    if (raycaster.ray.intersectPlane(groundPlane, hitPoint)) {
      mouseWorld.lerp(hitPoint, 0.06);
    }

    computeHeights(elapsed, mouseWorld.x, mouseWorld.z, mAmp);
    applyHeights();

    renderer.render(scene, camera);
    rafId = requestAnimationFrame(frame);
  };

  const io = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && !running) {
        running = true;
        last = performance.now();
        rafId = requestAnimationFrame(frame);
      } else if (!entry.isIntersecting && running) {
        running = false;
        cancelAnimationFrame(rafId);
      }
    },
    { threshold: 0.01 }
  );
  io.observe(mount);

  rafId = requestAnimationFrame(frame);
}
